import { NavigationButtons } from "@/components/NavigationButtons/NavigationButtons";
import {
  useCurrentUploadId,
  useCurrentUploadQuery,
  useUploadUpdateMutation,
} from "@/hooks/uploads";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Tooltip from "@ingka/tooltip";
import Button from "@ingka/button/Button";
import Text from "@ingka/text";
import questionMarkCircle from "@ingka/ssr-icon/paths/question-mark-circle";
import RadioButtonGroup from "@ingka/radio-button-group";
import { ValidationError } from "./ValidationError";

const DocumentFilesConfirmation = () => {
  const navigate = useNavigate();
  const uploadId = useCurrentUploadId();
  const uploadMutation = useUploadUpdateMutation();
  const currentUploadQuery = useCurrentUploadQuery();
  const [isValid, setIsValid] = useState(true);
  const [selectedRadioBtn, setSelectedRadioBtn] = useState<boolean | null>(
    null,
  );

  useEffect(() => {
    if (currentUploadQuery.data) {
      if (currentUploadQuery.data.allOriginalConfirmation !== null) {
        setSelectedRadioBtn(currentUploadQuery.data.allOriginalConfirmation);
        setIsValid(true);
      }
    }
  }, [currentUploadQuery.data]);

  const isPending = uploadMutation.isPending;

  const saveFilesConfirmation = async () => {
    if (selectedRadioBtn === null) {
      setIsValid(false);
      return;
    }

    await uploadMutation.mutateAsync({
      id: Number(uploadId),
      updateUploadDto: {
        allOriginalConfirmation: selectedRadioBtn,
      },
    });

    if (!isPending) {
      navigate(`/uploads/${uploadId}/mandatory-document-metadata`);
    }
  };

  return (
    <>
      <div className="mb-space100 flex w-[37rem] items-center gap-x-space25">
        <Text tagName="h2" headingSize="m" className="text-center text-lg">
          Are you requesting to upload a new document or a translated version of
          an already uploaded document?
        </Text>
        <Tooltip
          position="top"
          tooltipText="It is very important that we can link all assets to their originals in Ingka DAM, we need this information for statistics but also to make sure that we can find all assets if an issue comes up with the original and it needs to be edited or removed for legal reasons."
        >
          <Button
            iconOnly
            size="small"
            ssrIcon={questionMarkCircle}
            type="tertiary"
            aria-label="Information about the documents uploaded"
          />
        </Tooltip>
      </div>
      <RadioButtonGroup
        className="max-w-2xl"
        list={[
          {
            id: "original",
            label: "This is a new document",
            checked: selectedRadioBtn === true,
            onChange: () => {
              setSelectedRadioBtn(true);
              setIsValid(true);
            },
          },
          {
            id: "translation",
            label:
              "This is a local translation of a document that already exists in Ingka DAM",
            checked: selectedRadioBtn === false,
            onChange: () => {
              setSelectedRadioBtn(false);
              setIsValid(true);
            },
          },
        ]}
      />
      <section className="my-space250 ml-auto flex flex-row items-center">
        {!isValid && <ValidationError message="Choice is required" />}
        <NavigationButtons
          onPrev={() => {}}
          disabledPrev={true}
          nextButtonTitle={isPending ? "Processing..." : "Continue"}
          onContinue={() => saveFilesConfirmation()}
        />
      </section>
    </>
  );
};

export default DocumentFilesConfirmation;
